import { useEffect, useState } from 'react'

export interface ServerConfig {
  photoreal?: boolean
  maps_key?: string
  mbmt_tiles?: boolean
  mbmt_tiles_version?: string
}

let cached: Promise<ServerConfig> | null = null

function fetchConfig(): Promise<ServerConfig> {
  if (!cached) {
    cached = fetch('/api/config')
      .then((r) => r.json() as Promise<ServerConfig>)
      .catch((e) => {
        cached = null // let the next mount retry rather than freezing on one failure forever
        throw e
      })
  }
  return cached
}

export interface UseConfigResult extends ServerConfig {
  /** True once the fetch has settled (success or failure), never reverts to false --
   *  MbmtLiveMap's own map-construction gate needs this distinct from "config is {}
   *  because nothing has loaded yet" vs "config legitimately has no flags set". */
  loaded: boolean
}

/** Shared across every caller -- confirmed live 2026-10-10: MbmtLiveMap, StarMap,
 *  GlobalMap and EimPortsMap each independently fetched /api/config on every page load,
 *  for the exact same rarely-changing static flags (photoreal, maps_key, mbmt tile
 *  version). One of several redundant-fetch patterns compounding every site's page-load
 *  latency behind the browser's own per-origin connection limit (HTTP/1.1, confirmed live
 *  via performance.getEntriesByType('resource')[...].nextHopProtocol). This hook fetches
 *  once per page load, shared by every mounted consumer, instead of once per consumer. */
export function useConfig(): UseConfigResult {
  const [config, setConfig] = useState<ServerConfig>({})
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    let live = true
    fetchConfig()
      .then((c) => { if (live) setConfig(c) })
      .catch(() => { /* no config endpoint: callers' own flags simply stay unavailable */ })
      .finally(() => { if (live) setLoaded(true) })
    return () => { live = false }
  }, [])
  return { ...config, loaded }
}
